import React, { useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Table, Tag, Typography, message } from "@mimber/ui-web"
import { useMemberEvents } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { MemberEventRegistrationWithAttendanceDto } from "@mimber/schema"
import {
  EVENT_STATUS_DISPLAY,
  EVENT_LOCATION_TYPE_DISPLAY,
  EVENT_REGISTRATION_STATUS_DISPLAY,
  ATTENDANCE_STATUS_DISPLAY,
} from "@mimber/schema"
import type { MemberEventRegistrationsTableProps } from "./MemberEventRegistrationsTable.types"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const MemberEventRegistrationsTable: React.FunctionComponent<
  MemberEventRegistrationsTableProps
> = ({ memberUuid, className, ...tableProps }) => {
  const navigate = useNavigate()
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "member-event-registrations",
      defaultQueryParams: {
        page: 1,
        limit: 10,
      },
      scope: {
        memberUuid,
      },
    })

  const {
    data: response,
    isLoading,
    error,
  } = useMemberEvents(memberUuid, queryParams)

  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load member events: ${error.message}`)
    }
  }, [error])

  const columns: ColumnsType<MemberEventRegistrationWithAttendanceDto> =
    useMemo(
      () => [
        {
          title: "Event",
          dataIndex: ["event", "title"],
          key: "event.title",
          sorter: true,
          sortDirections: ["ascend", "descend"],
          ...getColumnSearchProps("Search by event title"),
          render: (_, record) => <Text>{record.event.title}</Text>,
        },
        {
          title: "Event Date",
          dataIndex: ["event", "startDate"],
          key: "event.startDate",
          sorter: true,
          sortDirections: ["ascend", "descend"],
          render: (_, record) => (
            <Text>{formatTableDate(record.event.startDate)}</Text>
          ),
        },
        {
          title: "Event Status",
          dataIndex: ["event", "status"],
          key: "event.status",
          render: (_, record) => {
            const display = EVENT_STATUS_DISPLAY[record.event.status]
            return <Tag color={display.color}>{display.label}</Tag>
          },
        },
        {
          title: "Location",
          dataIndex: ["event", "locationType"],
          key: "event.locationType",
          render: (_, record) => {
            const display =
              EVENT_LOCATION_TYPE_DISPLAY[record.event.locationType]
            return <Tag color={display.color}>{display.label}</Tag>
          },
        },
        {
          title: "Registration",
          dataIndex: "status",
          key: "status",
          render: (_, record) => {
            const display = EVENT_REGISTRATION_STATUS_DISPLAY[record.status]
            return <Tag color={display.color}>{display.label}</Tag>
          },
          filters: Object.entries(EVENT_REGISTRATION_STATUS_DISPLAY).map(
            ([value, { label }]) => ({ text: label, value })
          ),
        },
        {
          title: "Attendance",
          key: "attendance.status",
          render: (_, record) => {
            if (!record.attendance) {
              return <Tag color="default">N/A</Tag>
            }
            const display = ATTENDANCE_STATUS_DISPLAY[record.attendance.status]
            return <Tag color={display.color}>{display.label}</Tag>
          },
        },
        {
          title: "Check-In Time",
          key: "attendance.checkInTime",
          render: (_, record) => {
            if (!record.attendance?.checkInTime) {
              return <Text>-</Text>
            }
            return <Text>{formatTableDate(record.attendance.checkInTime)}</Text>
          },
        },
        {
          title: "Registered",
          dataIndex: "createdAt",
          key: "createdAt",
          sorter: true,
          sortDirections: ["ascend", "descend"],
          render: (createdAt: string) => (
            <Text>{formatTableDate(createdAt)}</Text>
          ),
        },
      ],
      []
    )

  const onTableChange: TableProps<MemberEventRegistrationWithAttendanceDto>["onChange"] =
    (pagination, filters, sorter) => {
      const tableChangeData = {
        pagination,
        filters: filters as Record<string, string[] | null>,
        sorter: {
          field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
          order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
        } as { field?: string | string[]; order?: "ascend" | "descend" },
      }
      handleTableChange(tableChangeData, setQueryParams, columns)
    }

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  return (
    <>
      <Table<MemberEventRegistrationWithAttendanceDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => {
            navigate({
              to: "/events/$eventUuid/$tabKey",
              params: { eventUuid: record.event.uuid, tabKey: "overview" },
            })
          },
          style: { cursor: "pointer" },
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
